import type { ModerationReportStatus, PaginatedResponse } from '@pulsify/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  type ModerationQueueParams,
  moderationApi,
  type ResolveModerationReportParams,
} from '~/entities/moderation/api/moderation-reports'
import { useSupabaseSession } from '~/entities/session/model/use-auth'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { log } from '~/shared/lib/logger'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { storageManager } from '~/shared/lib/storage-manager'

type ModerationPage = PaginatedResponse<{ id: string }>

function useModerationQueueEnabled() {
  const session = useSupabaseSession()
  return !!session && storageManager.isAdmin()
}

export function useMessageReports(status: ModerationReportStatus, page: number) {
  const enabled = useModerationQueueEnabled()
  const params: ModerationQueueParams = { status, page }
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_MODERATION_REPORTS, 'message', status, page),
    queryFn: () => moderationApi.listMessageReports(params),
    enabled,
  })
}

export function useUserReports(status: ModerationReportStatus, page: number) {
  const enabled = useModerationQueueEnabled()
  const params: ModerationQueueParams = { status, page }
  return useQuery({
    queryKey: queryKey(QueryKey.ADMIN_MODERATION_REPORTS, 'user', status, page),
    queryFn: () => moderationApi.listUserReports(params),
    enabled,
  })
}

export function useResolveModerationReport() {
  const queryClient = useQueryClient()
  const filters = { queryKey: queryKey(QueryKey.ADMIN_MODERATION_REPORTS) }

  return useMutation({
    mutationFn: (params: ResolveModerationReportParams) => moderationApi.resolve(params),
    onMutate: async ({ id }: ResolveModerationReportParams) => {
      await queryClient.cancelQueries(filters)
      const snapshot = queryClient.getQueriesData<ModerationPage>(filters)
      queryClient.setQueriesData<ModerationPage>(filters, (current) => {
        if (!current) return current
        const data = current.data.filter((report) => report.id !== id)
        if (data.length === current.data.length) return current
        const total = Math.max(0, current.pagination.total - 1)
        return {
          data,
          pagination: {
            ...current.pagination,
            total,
            totalPages: Math.ceil(total / current.pagination.pageSize),
          },
        }
      })
      return { snapshot }
    },
    onError: (error: unknown, _params, context) => {
      for (const [key, value] of context?.snapshot ?? []) {
        queryClient.setQueryData(key, value)
      }
      log.error(error as Error, '[Moderation] Resolve failed')
      toast.error(getErrorMessageFromError(error))
    },
    onSuccess: () => {
      toast.success(m.moderationReportReviewed())
    },
    onSettled: () => {
      queryClient.invalidateQueries(filters)
    },
  })
}
